import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { describirErrorServidor, consumeLoginNotice } from '../lib/api';
import { Loader2, Eye, EyeOff, Factory, AlertCircle, Info } from 'lucide-react';

export const Login = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  // Error visible bajo el formulario (persiste hasta el próximo intento; el toast puede pasar desapercibido)
  const [loginError, setLoginError] = useState(null);
  const [notice] = useState(() => consumeLoginNotice());
  const { login, sessionError } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError(null);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Bienvenido a CHIRINO STEEL');
      navigate('/dashboard');
    } catch (error) {
      const status = error.response?.status;
      const msg = describirErrorServidor(error);
      const titulo = status === 401 ? 'No se pudo iniciar sesión' : status === 429 ? 'Demasiados intentos' : 'El servidor no responde';
      setLoginError({ titulo, msg, status });
      toast.error(titulo, { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1697281679290-ad7be1b10682?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwc3RlZWwlMjBmYWN0b3J5JTIwaW50ZXJpb3IlMjB3ZWxkaW5nJTIwc3BhcmtzfGVufDB8fHx8MTc2OTg2NTE5NHww&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 to-slate-800/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
                <Factory className="w-7 h-7" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight">CHIRINO STEEL</h1>
                <p className="text-sm text-white/70">Ingeniería · Fabricación · Montaje</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <h2 className="font-heading text-4xl font-bold leading-tight">
              Sistema de Gestión<br />Operativa
            </h2>
            <p className="text-lg text-white/80 max-w-md">
              Control integral de órdenes de trabajo, proyectos y operaciones para la industria metalmecánica.
            </p>
            <div className="flex gap-8 pt-4">
              <div>
                <p className="font-heading text-3xl font-bold text-orange-400">100+</p>
                <p className="text-sm text-white/60">Proyectos</p>
              </div>
              <div>
                <p className="font-heading text-3xl font-bold text-blue-400">500+</p>
                <p className="text-sm text-white/60">Órdenes de Trabajo</p>
              </div>
              <div>
                <p className="font-heading text-3xl font-bold text-green-400">50+</p>
                <p className="text-sm text-white/60">Clientes</p>
              </div>
            </div>
          </div>
          
          <p className="text-sm text-white/50">© 2026 CHIRINO STEEL. Todos los derechos reservados.</p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <Factory className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="font-heading text-2xl font-bold">CHIRINO STEEL</span>
          </div>

          <Tabs defaultValue="login" className="w-full">

            <TabsContent value="login">
              <Card className="border-0 shadow-xl rounded-2xl">
                <CardHeader className="space-y-1 pb-4">
                  <CardTitle className="font-heading text-2xl">Bienvenido</CardTitle>
                  <CardDescription>Ingrese sus credenciales para acceder al sistema</CardDescription>
                </CardHeader>
                <CardContent>
                  {(notice || sessionError) && !loginError && (
                    <div role="status" data-testid="login-notice" className="mb-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                      <Info className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{notice || sessionError}</span>
                    </div>
                  )}
                  {loginError && (
                    <div role="alert" data-testid="login-error" className="mb-4 flex items-start gap-2 rounded-xl border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div>
                        <p className="font-semibold">{loginError.titulo}</p>
                        <p data-testid="login-error-detail">{loginError.msg}</p>
                        {loginError.status === 401 && (
                          <p className="mt-1 text-xs opacity-80">Verifica el correo y la contraseña. Si la cambiaste recientemente, usa la nueva (la guardada en el navegador puede estar desactualizada).</p>
                        )}
                      </div>
                    </div>
                  )}
                  <form onSubmit={handleLogin} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Correo Electronico</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="correo@empresa.com"
                        className="h-11 rounded-xl"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                        data-testid="login-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Contrasena</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          className={`h-11 rounded-xl ${loginError?.status === 401 ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
                          aria-invalid={loginError?.status === 401 || undefined}
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          data-testid="login-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg"
                          onClick={() => setShowPassword(!showPassword)}
                          data-testid="toggle-password"
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full h-11 rounded-xl text-sm font-semibold" disabled={loading} data-testid="login-submit">
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Iniciar Sesion
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
